import React, { useState } from "react";
import {
  Card,
  Tag,
  Button,
  Modal,
  Space,
  Badge,
  Tooltip,
  Skeleton,
  Empty,
  Divider,
} from "antd";
import {
  CompassOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  CarOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

export const QuotationCard = ({
  quote,
  onUseQuote,
  onPreviewQuote,
  loading = false,
  compact = false,
}) => {
  if (!quote) return null;

  const quoteId = quote.quotationNumber || quote.quoteNumber || quote._id?.slice(-6);
  const title =
    quote.title ||
    quote.meta?.proposalTitle ||
    `${quote.destination?.name || quote.destination?.city || quote.destinationSnapshot || "Tour"} Package`;

  const durationText =
    quote.durationText ||
    (quote.durationDays || quote.duration || quote.itinerary?.length
      ? `${quote.durationNights || Math.max(0, (quote.durationDays || quote.duration || quote.itinerary?.length || 1) - 1)}N / ${quote.durationDays || quote.duration || quote.itinerary?.length || 1}D`
      : "-");

  const grandTotal =
    quote.pricing?.grandTotal ||
    quote.pricing?.total ||
    quote.pricingSnapshot?.grandTotal ||
    quote.pricingSnapshot?.totalPrice ||
    0;

  // Extract brief route sequence from itinerary
  const routeCities = quote.itinerary
    ? Array.from(
        new Set(
          quote.itinerary
            .map((day) => day.city || day.title)
            .filter(Boolean)
        )
      )
    : [];

  const routeSummary =
    routeCities.length > 0
      ? routeCities.join(" ➔ ")
      : quote.destinationSnapshot ||
        quote.destination?.name ||
        quote.destination?.city ||
        "Custom Tour Route";

  // Extract hotels summary
  const hotelsList = quote.hotels || quote.itinerary?.flatMap((d) => d.hotels || []) || [];
  const hotelNames = Array.from(new Set(hotelsList.map((h) => h.name || h.hotelName).filter(Boolean)));

  // Extract vehicle summary
  const transportList = quote.transport || quote.itinerary?.flatMap((d) => d.transport || []) || [];
  const vehicleTypes = Array.from(
    new Set(
      transportList
        .map((t) => t.vehicleType || t.vehicle || t.routeName)
        .filter(Boolean)
    )
  );

  return (
    <Card
      className="hover:shadow-lg transition-all duration-300 border border-emerald-100/80 rounded-2xl overflow-hidden bg-gradient-to-br from-white via-slate-50/50 to-emerald-50/20"
      bodyStyle={{ padding: compact ? "16px" : "20px" }}
      actions={[
        <Button
          key="preview"
          type="text"
          icon={<EyeOutlined className="text-slate-500" />}
          className="text-xs font-semibold text-slate-600 hover:text-emerald-700"
          onClick={() => onPreviewQuote && onPreviewQuote(quote)}
        >
          Preview Details
        </Button>,
        <Button
          key="use"
          type="primary"
          icon={<ThunderboltOutlined />}
          className="bg-emerald-600 hover:bg-emerald-700 border-none shadow-md shadow-emerald-600/20 rounded-xl text-xs font-bold px-4"
          onClick={() => onUseQuote && onUseQuote(quote)}
        >
          Use This Quote
        </Button>,
      ]}
    >
      {/* Top Bar: Badge & Quote ID */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Tag color="emerald" className="m-0 font-bold px-2.5 py-0.5 rounded-full border-none text-[11px] bg-emerald-100 text-emerald-800">
            {durationText}
          </Tag>
          <Tag color="blue" className="m-0 font-medium px-2 py-0.5 rounded-full text-[11px]">
            #{quoteId}
          </Tag>
        </div>
        <span className="text-[11px] text-slate-400 font-medium">
          {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString() : ""}
        </span>
      </div>

      {/* Main Title */}
      <h3 className="text-base font-bold text-slate-900 line-clamp-1 mb-2 tracking-tight group-hover:text-emerald-700 transition-colors">
        {title}
      </h3>

      {/* Route Flow */}
      <div className="bg-white/80 border border-slate-100 rounded-xl p-2.5 mb-3">
        <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700/80 flex items-center gap-1 mb-1">
          <CompassOutlined /> Route Highlights
        </div>
        <div className="text-xs font-semibold text-slate-700 line-clamp-2 leading-relaxed">
          {routeSummary}
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-slate-100/70 rounded-lg p-2 flex items-center gap-2">
          <HomeOutlined className="text-emerald-600" />
          <span className="text-slate-600 font-medium truncate">
            {hotelNames.length > 0 ? `${hotelNames.length} Hotel(s)` : "Hotels Included"}
          </span>
        </div>
        <div className="bg-slate-100/70 rounded-lg p-2 flex items-center gap-2">
          <CarOutlined className="text-blue-600" />
          <span className="text-slate-600 font-medium truncate">
            {vehicleTypes.length > 0 ? vehicleTypes[0] : "Private Cab"}
          </span>
        </div>
      </div>

      {/* Price Banner */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2.5 mt-1">
        <div>
          <div className="text-[10px] uppercase font-semibold text-slate-400">Total Price</div>
          <div className="text-lg font-black text-slate-900 tracking-tight">
            ₹{Number(grandTotal).toLocaleString("en-IN")}
          </div>
        </div>
        {quote.travelers && (
          <div className="text-right text-[11px] text-slate-500 font-medium">
            <UserOutlined className="mr-1 text-slate-400" />
            {quote.travelers.adults || 1} Adult(s)
            {quote.travelers.children ? `, ${quote.travelers.children} Child` : ""}
          </div>
        )}
      </div>
    </Card>
  );
};

const SuggestedQuotesSection = ({
  quotes = [],
  loading = false,
  destinationName = "",
  onUseQuote,
  className = "",
}) => {
  const [previewQuote, setPreviewQuote] = useState(null);

  if (loading) {
    return (
      <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 my-4">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (!quotes || quotes.length === 0) {
    return null;
  }

  return (
    <div className={`my-4 bg-gradient-to-r from-emerald-900/5 via-teal-900/5 to-slate-900/5 border border-emerald-200/60 rounded-3xl p-5 shadow-sm ${className}`}>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs mb-1">
            <ThunderboltOutlined className="text-emerald-600" />
            Suggested Tour Quotes {destinationName ? `for ${destinationName}` : ""}
          </div>
          <h2 className="text-lg font-bold text-slate-900 m-0">
            Select a Pre-Built Quote Template
          </h2>
          <p className="text-xs text-slate-500 m-0 mt-0.5">
            Click "Use This Quote" to instantly load full routes, hotels, transport & itinerary into your editor.
          </p>
        </div>
        <Badge count={`${quotes.length} Templates`} style={{ backgroundColor: "#059669" }} />
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {quotes.map((quote) => (
          <QuotationCard
            key={quote._id}
            quote={quote}
            onUseQuote={onUseQuote}
            onPreviewQuote={(q) => setPreviewQuote(q)}
          />
        ))}
      </div>

      {/* Preview Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-slate-900 font-bold">
            <CompassOutlined className="text-emerald-600" />
            <span>{previewQuote?.title || "Quotation Preview"}</span>
            <Tag color="emerald" className="m-0 font-semibold">
              #{previewQuote?.quotationNumber || previewQuote?.quoteNumber}
            </Tag>
          </div>
        }
        open={!!previewQuote}
        onCancel={() => setPreviewQuote(null)}
        width={720}
        footer={[
          <Button key="close" onClick={() => setPreviewQuote(null)}>
            Close
          </Button>,
          <Button
            key="use"
            type="primary"
            icon={<ThunderboltOutlined />}
            className="bg-emerald-600 hover:bg-emerald-700 border-none shadow-md font-bold px-5"
            onClick={() => {
              const q = previewQuote;
              setPreviewQuote(null);
              if (onUseQuote) onUseQuote(q);
            }}
          >
            Use This Quote Template
          </Button>,
        ]}
      >
        {previewQuote && (
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex justify-between items-center">
              <div>
                <div className="text-xs text-slate-400 font-medium uppercase">Duration & Destination</div>
                <div className="text-sm font-bold text-slate-800">
                  {previewQuote.durationText || `${previewQuote.duration || 1} Days`} • {previewQuote.destinationSnapshot || previewQuote.destination?.name || "Custom"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 font-medium uppercase">Package Price</div>
                <div className="text-xl font-black text-emerald-700">
                  ₹{(previewQuote.pricing?.grandTotal || previewQuote.pricing?.total || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            {/* Itinerary Preview */}
            {previewQuote.itinerary?.length > 0 && (
              <div>
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-1.5 text-sm">
                  <CalendarOutlined className="text-emerald-600" /> Day-by-Day Itinerary
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">

{previewQuote.itinerary.map((day, idx) => (
  <div
    key={idx}
    className="bg-white border border-slate-200 rounded-xl p-3 text-xs"
  >
    <div className="font-bold text-slate-900 flex items-center justify-between">
      <span>
        Day {day.dayNumber || idx + 1}: {day.title}
      </span>

      {day.city && (
        <Tag color="blue" className="m-0 text-[10px]">
          {day.city}
        </Tag>
      )}
    </div>

    {day.description && (
      <p className="text-slate-600 mt-1 mb-2">
        {day.description}
      </p>
    )}

    {day.activities?.length > 0 && (
      <>
        <Divider orientation="left" plain>
          Activities
        </Divider>

        <ul className="list-disc ml-5">
          {day.activities.map((activity, i) => (
            <li key={i}>
              {activity.name ||
                activity.title ||
                activity.activityName}
            </li>
          ))}
        </ul>
      </>
    )}

    {day.hotels?.length > 0 && (
      <>
        <Divider orientation="left" plain>
          Hotels
        </Divider>

        <ul className="list-disc ml-5">
          {day.hotels.map((hotel, i) => (
            <li key={i}>
              {hotel.hotelName ||
                hotel.name}
            </li>
          ))}
        </ul>
      </>
    )}

    {day.transport?.length > 0 && (
      <>
        <Divider orientation="left" plain>
          Transport
        </Divider>

        <ul className="list-disc ml-5">
          {day.transport.map((item, i) => (
            <li key={i}>
              {item.vehicleType ||
                item.vehicle ||
                item.routeName}
            </li>
          ))}
        </ul>
      </>
    )}

    {day.notes && (
      <>
        <Divider orientation="left" plain>
          Notes
        </Divider>

        <p>{day.notes}</p>
      </>
    )}

    {day.specialInclusions?.length > 0 && (
      <>
        <Divider orientation="left" plain>
          Special Inclusions
        </Divider>

        <ul className="list-disc ml-5">
          {day.specialInclusions.map((item, i) => (
            <li key={i}>
              {typeof item === "string"
                ? item
                : item.title ||
                  item.name}
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
))}

                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuggestedQuotesSection;
